import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDisabledReasons,
  createDisabledReason,
  updateDisabledReason,
  deleteDisabledReason,
  DisabledReason,
} from '../services/disabledReasonsService';
import { Plus, Edit, Trash2, X, Save } from 'lucide-react';
import './DisabledReasonsScreen.css';

export default function DisabledReasonsScreen() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    message: '',
    isActive: true,
    order: 0,
  });

  const { data: reasons, isLoading } = useQuery({
    queryKey: ['disabledReasons'],
    queryFn: getDisabledReasons,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createDisabledReason(
        formData.code,
        formData.title,
        formData.message,
        formData.isActive,
        formData.order
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disabledReasons'] });
      queryClient.invalidateQueries({ queryKey: ['activeDisabledReasons'] });
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Error creating disabled reason:', error);
      alert(error.message || 'Failed to create disabled reason. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) =>
      updateDisabledReason(id, {
        code: formData.code,
        title: formData.title,
        message: formData.message,
        isActive: formData.isActive,
        order: formData.order,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disabledReasons'] });
      queryClient.invalidateQueries({ queryKey: ['activeDisabledReasons'] });
      setEditingId(null);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Error updating disabled reason:', error);
      alert(error.message || 'Failed to update disabled reason. Please try again.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDisabledReason,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disabledReasons'] });
      queryClient.invalidateQueries({ queryKey: ['activeDisabledReasons'] });
    },
    onError: (error: any) => {
      console.error('Error deleting disabled reason:', error);
      alert(error.message || 'Failed to delete disabled reason. Please try again.');
    },
  });

  const resetForm = () => {
    setFormData({
      code: '',
      title: '',
      message: '',
      isActive: true,
      order: 0,
    });
  };

  const handleEdit = (reason: DisabledReason) => {
    setEditingId(reason.id);
    setFormData({
      code: reason.code,
      title: reason.title,
      message: reason.message,
      isActive: reason.isActive,
      order: reason.order || 0,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowCreateModal(false);
    resetForm();
  };

  const handleSave = () => {
    if (!canSave) {
      alert('Please fill in all required fields (code, title, and message)');
      return;
    }

    // Validate code format (lowercase, letters, numbers, underscores only)
    const codeRegex = /^[a-z0-9_]+$/;
    if (!codeRegex.test(formData.code.trim())) {
      alert('Code must be lowercase and contain only letters, numbers, and underscores (e.g., payment_failed)');
      return;
    }

    if (editingId) {
      updateMutation.mutate(editingId);
    } else {
      createMutation.mutate();
    }
  };

  const canSave = formData.code.trim() && formData.title.trim() && formData.message.trim();

  return (
    <div className="disabled-reasons-screen">
      <div className="screen-header">
        <div>
          <h1>Disabled Reasons</h1>
          <p>Manage reasons for account deactivation</p>
        </div>
        <button
          className="primary-button"
          onClick={() => {
            setShowCreateModal(true);
            setEditingId(null);
            resetForm();
          }}
        >
          <Plus size={20} />
          <span>Create Reason</span>
        </button>
      </div>

      {isLoading ? (
        <div className="loading-state">Loading disabled reasons...</div>
      ) : (
        <div className="reasons-table-container">
          <table className="reasons-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Title</th>
                <th>Message</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reasons && reasons.length > 0 ? (
                reasons.map((reason) => (
                  <tr key={reason.id} className={editingId === reason.id ? 'editing' : ''}>
                    {editingId === reason.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                            placeholder="e.g., payment_failed"
                            disabled={updateMutation.isPending}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Title"
                            disabled={updateMutation.isPending}
                          />
                        </td>
                        <td>
                          <textarea
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            placeholder="Message"
                            rows={2}
                            disabled={updateMutation.isPending}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={formData.order}
                            onChange={(e) =>
                              setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                            }
                            disabled={updateMutation.isPending}
                          />
                        </td>
                        <td>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={formData.isActive}
                              onChange={(e) =>
                                setFormData({ ...formData, isActive: e.target.checked })
                              }
                              disabled={updateMutation.isPending}
                            />
                            Active
                          </label>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="icon-button save"
                              onClick={handleSave}
                              disabled={!canSave || updateMutation.isPending}
                              title="Save"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              className="icon-button cancel"
                              onClick={handleCancel}
                              disabled={updateMutation.isPending}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          <code className="code-badge">{reason.code}</code>
                        </td>
                        <td>{reason.title}</td>
                        <td className="message-cell">{reason.message}</td>
                        <td>{reason.order || 0}</td>
                        <td>
                          <span className={`status-badge ${reason.isActive ? 'active' : 'inactive'}`}>
                            {reason.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="icon-button edit"
                              onClick={() => handleEdit(reason)}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="icon-button delete"
                              onClick={() => {
                                if (confirm(`Delete "${reason.title}"? This cannot be undone.`)) {
                                  deleteMutation.mutate(reason.id);
                                }
                              }}
                              disabled={deleteMutation.isPending}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No disabled reasons found. Create one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Disabled Reason</h2>
              <button className="close-button" onClick={handleCancel}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="e.g., payment_failed"
                  disabled={createMutation.isPending}
                />
                <small>Unique identifier (lowercase, underscores)</small>
              </div>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Payment Method Failed"
                  disabled={createMutation.isPending}
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Message shown to users"
                  rows={4}
                  disabled={createMutation.isPending}
                />
              </div>
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) =>
                    setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                  }
                  disabled={createMutation.isPending}
                />
                <small>Lower numbers appear first</small>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    disabled={createMutation.isPending}
                  />
                  Active (available for selection)
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="secondary-button"
                onClick={handleCancel}
                disabled={createMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                onClick={handleSave}
                disabled={!canSave || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

